/* =====================================================================
 * OrgBreakdown — 본부 → 부서 usage, ranked.
 *
 * Form: horizontal bars in descending order, one row per 부서, grouped
 * under their 본부. Ranked bars rather than a pie or a stacked column
 * because the question is "who used the most", which is a comparison of
 * lengths against a shared baseline — the one encoding people read
 * accurately. Dozens of departments also rules out any encoding that
 * needs a distinct colour per category.
 *
 * Magnitude is one hue at full strength; the bar's *length* carries the
 * value and its label carries the identity. Selecting a row filters the
 * chart above rather than drilling into a separate view, so the time
 * shape and the ranking stay on screen together.
 * ===================================================================== */

import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type { AdminDashboardBreakdown } from '@auw/api-client';

import { CATEGORICAL, type ChartMode } from '../dashboard/chartPalette';
import { formatDashboardTokenCount } from '../dashboard/dashboardMetrics';
import { EmptyState } from '../dashboard/dashboard.styles';

const OrgGroup = styled.div`
  & + & {
    margin-top: 18px;
    padding-top: 18px;
    border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
  }
`;

const OrgHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
`;

const OrgName = styled.h3`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 650;
  color: ${({ theme }) => theme.colors.textPrimary};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OrgTotal = styled.span`
  flex-shrink: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-variant-numeric: tabular-nums;
`;

const Row = styled.button<{ $selected: boolean }>`
  display: grid;
  grid-template-columns: minmax(88px, 1.1fr) minmax(0, 2.4fr) auto;
  align-items: center;
  gap: 10px;
  width: 100%;
  border: none;
  background: ${({ theme, $selected }) =>
    $selected ? theme.colors.bgHover : 'transparent'};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  padding: 6px 8px;
  cursor: pointer;
  text-align: left;
  font: inherit;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.brand};
    outline-offset: 1px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: minmax(0, 1fr) auto;
  }
`;

const DeptName = styled.span`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* The bar track is hidden on mobile: at that width it is too short to
 * compare lengths, and the number beside it already answers the question. */
const Track = styled.div`
  height: 8px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.bgInput};
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: none;
  }
`;

const Fill = styled.div<{ $color: string; $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => Math.max($pct, 0.5)}%;
  background: ${({ $color }) => $color};
  border-radius: 4px;
`;

const DeptValue = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const Meta = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-left: 6px;
  font-weight: 500;
`;

export interface OrgBreakdownProps {
  data: AdminDashboardBreakdown;
  mode: ChartMode;
  selected: { orgCode?: string; departmentCode?: string };
  onSelect: (next: { orgCode?: string; departmentCode?: string }) => void;
  /** Renders ``unknown`` codes with a human label instead of the raw token. */
  labelFor: (code: string, name: string | null) => string;
}

export const OrgBreakdown: React.FC<OrgBreakdownProps> = ({
  data,
  mode,
  selected,
  onSelect,
  labelFor,
}) => {
  const { t, i18n } = useTranslation();

  if (!data.orgs.length) {
    return <EmptyState>{t('common:adminDashboard.noUsageYet')}</EmptyState>;
  }

  const color = CATEGORICAL[mode][0];
  const numberFormat = new Intl.NumberFormat(i18n.language);
  /* Bars are scaled against the largest 부서 in the window, not against the
   * tenant total: against the total, every row in a large org collapses to a
   * sliver and the ranking becomes unreadable. */
  const peak = Math.max(
    1,
    ...data.orgs.flatMap((org) =>
      org.departments.map((dept) => dept.total_token_count),
    ),
  );

  return (
    <>
      {data.orgs.map((org) => {
        const orgSelected = selected.orgCode === org.org_code;
        return (
          <OrgGroup key={org.org_code}>
            <OrgHead>
              <OrgName
                title={labelFor(org.org_code, org.org_name)}
              >
                {labelFor(org.org_code, org.org_name)}
              </OrgName>
              <OrgTotal>
                {formatDashboardTokenCount(org.total_token_count)}
                <Meta>
                  {t('common:adminDashboard.orgMeta', {
                    departments: org.departments.length,
                    users: org.user_count,
                  })}
                </Meta>
              </OrgTotal>
            </OrgHead>
            {org.departments.map((dept) => {
              const isSelected =
                orgSelected && selected.departmentCode === dept.department_code;
              const name = labelFor(dept.department_code, dept.department_name);
              const exact = `${numberFormat.format(dept.total_token_count)} ${t(
                'common:dashboard.unitTokens',
              )}`;
              return (
                <Row
                  key={dept.department_code}
                  type="button"
                  $selected={isSelected}
                  aria-pressed={isSelected}
                  title={`${name} · ${exact}`}
                  onClick={() =>
                    onSelect(
                      isSelected
                        ? {}
                        : {
                            orgCode: org.org_code,
                            departmentCode: dept.department_code,
                          },
                    )
                  }
                >
                  <DeptName>{name}</DeptName>
                  <Track aria-hidden>
                    <Fill
                      $color={color}
                      $pct={(dept.total_token_count / peak) * 100}
                    />
                  </Track>
                  <DeptValue>
                    {formatDashboardTokenCount(dept.total_token_count)}
                    <Meta>
                      {t('common:adminDashboard.deptMeta', {
                        runs: dept.total_runs,
                        users: dept.user_count,
                      })}
                    </Meta>
                  </DeptValue>
                </Row>
              );
            })}
          </OrgGroup>
        );
      })}
    </>
  );
};
